// src/components/AvailabilityEditor.tsx
import { useState, type KeyboardEvent } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type DraftAvailability =
  | { kind: "single"; date: string; start: string; end: string }
  | { kind: "range"; startDate: string; startTime: string; endDate: string; endTime: string }
  | { kind: "recurring"; days: string[]; fromDate: string; startTime: string; endTime: string; untilDate: string };

export default function AvailabilityEditor({
  value,
  onChange,
}: {
  value: DraftAvailability[];
  onChange: (v: DraftAvailability[]) => void;
}) {
  const [tab, setTab] = useState<"single" | "range" | "recurring">("single");

  // lokale felter
  const [single, setSingle] = useState({ date: "", start: "08:00", end: "16:00" });
  const [range, setRange] = useState({ startDate: "", startTime: "00:00", endDate: "", endTime: "23:59" });
  const [rec, setRec] = useState({ days: [] as string[], fromDate: "", startTime: "07:30", endTime: "16:00", untilDate: "" });

  const addSingle = () => {
    if (!single.date || !single.start || !single.end) return;
    onChange([...value, { kind: "single", ...single }]);
    setSingle({ date: "", start: "08:00", end: "16:00" });
  };

  const addRange = () => {
    if (!range.startDate || !range.startTime || !range.endDate || !range.endTime) return;
    onChange([...value, { kind: "range", ...range }]);
    setRange({ startDate: "", startTime: "00:00", endDate: "", endTime: "23:59" });
  };

  const addRec = () => {
    if (!rec.fromDate || !rec.startTime || !rec.endTime || !rec.untilDate || rec.days.length === 0) return;
    onChange([...value, { kind: "recurring", ...rec }]);
    setRec({ days: [], fromDate: "", startTime: "07:30", endTime: "16:00", untilDate: "" });
  };

  const removeIdx = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  const toggleDay = (d: string) =>
    setRec((s) => ({
      ...s,
      days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d],
    }));

  // Stop ENTER i editoren fra at submitte hele CreateListing-formen
  const stopEnter = (e: KeyboardEvent) => {
    if (e.key === "Enter") e.preventDefault();
  };

  return (
    <div className="space-y-4" onKeyDown={stopEnter}>
      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="single" asChild>
            <button type="button">Enkeltdage</button>
          </TabsTrigger>
          <TabsTrigger value="range" asChild>
            <button type="button">Interval</button>
          </TabsTrigger>
          <TabsTrigger value="recurring" asChild>
            <button type="button">Gentagende</button>
          </TabsTrigger>
        </TabsList>

        {/* SINGLE */}
        <TabsContent value="single" className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Dato</Label>
              <Input type="date" value={single.date} onChange={(e) => setSingle({ ...single, date: e.target.value })} />
            </div>
            <div>
              <Label>Start</Label>
              <Input type="time" value={single.start} onChange={(e) => setSingle({ ...single, start: e.target.value })} />
            </div>
            <div>
              <Label>Slut</Label>
              <Input type="time" value={single.end} onChange={(e) => setSingle({ ...single, end: e.target.value })} />
            </div>
          </div>
          <Button type="button" onClick={addSingle}>Tilføj enkelt-vindue</Button>
        </TabsContent>

        {/* RANGE */}
        <TabsContent value="range" className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Startdato</Label>
              <Input type="date" value={range.startDate} onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
            </div>
            <div>
              <Label>Starttid</Label>
              <Input type="time" value={range.startTime} onChange={(e) => setRange({ ...range, startTime: e.target.value })} />
            </div>
            <div>
              <Label>Slutdato</Label>
              <Input type="date" value={range.endDate} onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
            </div>
            <div>
              <Label>Sluttid</Label>
              <Input type="time" value={range.endTime} onChange={(e) => setRange({ ...range, endTime: e.target.value })} />
            </div>
          </div>
          <Button type="button" onClick={addRange}>Tilføj interval</Button>
        </TabsContent>

        {/* RECURRING */}
        <TabsContent value="recurring" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {["MO","TU","WE","TH","FR","SA","SU"].map((d) => (
              <Button
                key={d}
                type="button"
                variant={rec.days.includes(d) ? "default" : "outline"}
                onClick={() => toggleDay(d)}
              >
                {d}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Fra dato</Label>
              <Input type="date" value={rec.fromDate} onChange={(e) => setRec({ ...rec, fromDate: e.target.value })} />
            </div>
            <div>
              <Label>Starttid</Label>
              <Input type="time" value={rec.startTime} onChange={(e) => setRec({ ...rec, startTime: e.target.value })} />
            </div>
            <div>
              <Label>Sluttid</Label>
              <Input type="time" value={rec.endTime} onChange={(e) => setRec({ ...rec, endTime: e.target.value })} />
            </div>
            <div>
              <Label>Gentag indtil</Label>
              <Input type="date" value={rec.untilDate} onChange={(e) => setRec({ ...rec, untilDate: e.target.value })} />
            </div>
          </div>
          <Button type="button" onClick={addRec}>Tilføj gentagende</Button>
        </TabsContent>
      </Tabs>

      {value.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Tilføjede vinduer</Label>
          <ul className="space-y-1 text-sm">
            {value.map((v, i) => (
              <li key={i} className="flex items-center justify-between rounded border px-3 py-2">
                <span>
                  {v.kind === "single" && `Enkeltdag: ${v.date} ${v.start}–${v.end}`}
                  {v.kind === "range" && `Interval: ${v.startDate} ${v.startTime} → ${v.endDate} ${v.endTime}`}
                  {v.kind === "recurring" && `Gentagende: [${v.days.join(", ")}] fra ${v.fromDate} ${v.startTime}–${v.endTime} til ${v.untilDate}`}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => removeIdx(i)}>
                  Fjern
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
